import { useCallback, useEffect, useState } from 'react';
import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';

export type FleetAlertRow = {
  id: string;
  busId: string;
  busName: string;
  type: string;
  message: string;
  timestamp: Date;
  read: boolean;
  /* Optional extras carried by the safety alerts added 2026-07-30. Every
     existing alert type simply leaves them undefined, so nothing that reads
     this row today has to change — but a parent's disputed drop-off is useless
     to an office that cannot see WHICH CHILD, and re-fetching the document to
     find out would mean a read per row on a list of 500. */
  childName?: string;
  /** 'zana' on paid schools, 'admin' on basic — see DropOffReviewService. */
  triage?: string;
  /** Where the bus actually was, when the alert is about a place. */
  dropLat?: number;
  dropLng?: number;
  /** What the parent typed, when they typed anything. */
  note?: string;
  /* WHY a refuel was flagged — written by the app onto every
     `fuel_needs_review` alert and, until 2026-08-30, dropped right here on the
     way in. The app words 4 of its 8 reasons and leaves the rest as "this
     refuel needs review", so without this field the dashboard cannot tell an
     owner what is wrong with a fill even though the answer is on the document.
     See REVIEW_REASON_COPY in alertMeta.ts. */
  reviewReason?: string;
  /** 'needs_review' | 'ok' | … — the app's own verdict on the fill. */
  efficiencyFlag?: string;
};

/** Firestore rejects a batch over 500 writes. */
const BATCH_LIMIT = 450;

export function useFleetAlerts(fleetOrgCode: string | null) {
  const [alerts, setAlerts] = useState<FleetAlertRow[]>([]);

  useEffect(() => {
    if (!fleetOrgCode) {
      setAlerts([]);
      return;
    }
    /* Sorted server-side on the fleetAlerts(schoolCode ASC, timestamp DESC)
       composite index — deployed to pear-bc51f on 2026-07-25. Before that this
       query was rejected outright and the old handler swallowed the error into
       an empty list, so the lorry console showed 0 alerts for every org.

       Cap is 500, not the old 40: this list also drives `unreadCount` and
       `dismissAllUnread`, which a tight cap silently truncates. */
    const q = query(
      collection(db, 'fleetAlerts'),
      where('schoolCode', '==', fleetOrgCode),
      orderBy('timestamp', 'desc'),
      limit(500),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setAlerts(
          snap.docs
            .map((d) => {
              const data = d.data() as Record<string, unknown>;
              const ts = data.timestamp as Timestamp | undefined;
              return {
                id: d.id,
                busId: (data.busId as string) || '',
                busName: (data.busName as string) || '',
                type: (data.type as string) || 'info',
                message: (data.message as string) || '',
                timestamp: ts?.toDate?.() ?? new Date(),
                read: (data.read as boolean) ?? false,
                childName: (data.childName as string) || undefined,
                triage: (data.triage as string) || undefined,
                dropLat: typeof data.dropLat === 'number' ? data.dropLat : undefined,
                dropLng: typeof data.dropLng === 'number' ? data.dropLng : undefined,
                note: (data.note as string) || undefined,
                reviewReason: (data.reviewReason as string) || undefined,
                efficiencyFlag: (data.efficiencyFlag as string) || undefined,
              };
            }),
        );
      },
      // Never swallow this: a silent handler is what hid the missing index.
      (err) => console.error('[useFleetAlerts] snapshot error:', err),
    );
    return () => unsub();
  }, [fleetOrgCode]);

  const dismissAlert = useCallback(async (alertId: string) => {
    await updateDoc(doc(db, 'fleetAlerts', alertId), { read: true });
  }, []);

  const dismissAllUnread = useCallback(async () => {
    const unread = alerts.filter((a) => !a.read);
    if (unread.length === 0) return;
    // Chunked: the list is no longer capped at 40, so a busy org can exceed
    // Firestore's 500-write batch ceiling.
    for (let i = 0; i < unread.length; i += BATCH_LIMIT) {
      const batch = writeBatch(db);
      unread.slice(i, i + BATCH_LIMIT).forEach((a) => batch.update(doc(db, 'fleetAlerts', a.id), { read: true }));
      await batch.commit();
    }
  }, [alerts]);

  return {
    alerts,
    unreadCount: alerts.filter((a) => !a.read).length,
    dismissAlert,
    dismissAllUnread,
  };
}
